import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React from 'react';
import { ActionSheet } from '@/components/action-sheet';
import { gql, useMutation } from '@apollo/client';
import {
  ORDER_INBOUNDS_LIST_QUERY,
  useOrderInboundListQueryVariables,
} from './queries';
import {
  OrderInbounds_ListQuery,
  OrderInbounds_ListQueryVariables,
  SmartOrder_RejectMutation,
  SmartOrder_RejectMutationVariables,
} from '@/types';
import { partnerSupplier } from '@/utils/environment';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useToasts } from '@/contexts/toast-context';

export const REMOVE_ORDER_INBOUND = gql`
  mutation SmartOrder_Reject(
    $businessOrganizationUUID: uuid!
    $smartOrdersToRemove: [uuid!]!
    $updatedAt: timestamptz!
    $updatedBy: uuid!
  ) {
    update_smart_order(
      where: {
        supplier: {
          business_organization_uuid: { _eq: $businessOrganizationUUID }
        }
        uuid: { _in: $smartOrdersToRemove }
      }
      _set: {
        status: "REJECTED"
        updated_at: $updatedAt
        updated_by_uuid: $updatedBy
      }
    ) {
      returning {
        status
        updated_at
        uuid
      }
    }
  }
`;

interface Props {
  onBeforeRemove?: () => void;
  onClose: () => void;
  onRemove: () => void;
  smartOrderUUIDs: string[];
}

export const RemoveOrderInboundActionSheet: React.FC<Props> = ({
  onClose,
  onBeforeRemove = () => {},
  onRemove,
  smartOrderUUIDs,
}) => {
  const { user } = useAuthenticatedUserContext();
  const { showErrorToast } = useToasts();

  const queryVariables = useOrderInboundListQueryVariables();

  const [removeOrderInbound, { loading }] = useMutation<
    SmartOrder_RejectMutation,
    SmartOrder_RejectMutationVariables
  >(REMOVE_ORDER_INBOUND, {
    update: (client, { data: mutationData }) => {
      const mutatedOrders = mutationData?.update_smart_order?.returning ?? [];
      const mutatedIds = mutatedOrders.reduce<Record<string, boolean>>(
        (acc, order) => {
          acc[order.uuid] = true;
          return acc;
        },
        {},
      );
      const cachedData = client.readQuery<
        OrderInbounds_ListQuery,
        OrderInbounds_ListQueryVariables
      >({
        query: ORDER_INBOUNDS_LIST_QUERY,
        variables: queryVariables,
      });

      if (cachedData) {
        client.writeQuery<
          OrderInbounds_ListQuery,
          OrderInbounds_ListQueryVariables
        >({
          data: {
            ...cachedData,
            smart_order: cachedData.smart_order.filter(
              (order) => !mutatedIds[order.uuid],
            ),
          },
          query: ORDER_INBOUNDS_LIST_QUERY,
          variables: queryVariables,
        });
      }
    },
  });

  const actions = [i18n.t('back'), i18n.t('remove')];

  return (
    <ActionSheet
      actions={actions}
      dangerIndices={[1]}
      isVisible
      loadingActionIndex={loading ? 1 : null}
      message={i18n.t('remove_order_inbound.confirmation_message')}
      onAction={async (action) => {
        const updatedAt = new Date().toISOString();

        if (action === actions[1]) {
          Analytics.track('remove_order_inbound_confirmation_button.press');
          try {
            onBeforeRemove();
            await removeOrderInbound({
              variables: {
                businessOrganizationUUID:
                  partnerSupplier.businessOrganizationUUID,
                smartOrdersToRemove: smartOrderUUIDs,
                updatedAt,
                updatedBy: user.uuid,
              },
            });
            onRemove();
            Analytics.track('remove_order_inbound_confirmation.success', {
              smartOrderUUIDs,
              updatedAt,
              updatedBy: user.uuid,
            });
          } catch (error) {
            Analytics.track('remove_order_inbound_confirmation.error', {
              error,
              smartOrderUUIDs,
            });
            showErrorToast();
          }
        }
        onClose();
      }}
      onClose={onClose}
      testID="remove-order-inbound-action-sheet"
      title={i18n.t('remove_order_inbound.confirmation_title')}
    />
  );
};
